import React, { useEffect, useState } from 'react';
import LionField from './LionField';
import BrandTicker from './BrandTicker';
import StarField from './StarField';
import '../styles/auth.css';

/**
 * Shared frame for the sign-in and reset-password screens: the form sits
 * directly on a black stage (no card, no borders) beside the interactive
 * ember-lion crest (see LionField). On a phone the crest becomes a header and
 * the form fills the rest of the screen.
 */

const svg = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };

export const IconEye = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" {...svg}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
export const IconEyeOff = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" {...svg}>
    <path d="M3 3l18 18" />
    <path d="M10.6 5.1A10.6 10.6 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.1M6.6 6.6A17.3 17.3 0 0 0 2 12s3.6 7 10 7a10 10 0 0 0 4.4-1" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);
export const IconArrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...svg} strokeWidth={2.2}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
export const IconAlert = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...svg} strokeWidth={2}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5h.01" />
  </svg>
);
export const IconCheck = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svg} strokeWidth={2.4}>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
export const IconCaps = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" {...svg} strokeWidth={2}>
    <path d="M12 4l8 9h-5v5H9v-5H4z" />
    <path d="M9 21h6" />
  </svg>
);

/**
 * A labelled field with no container: small caps label + ember, big type on a
 * soft fade. `children` is the <input className="auth-input">; `side` sits on
 * the label row (e.g. "Forgot?"); `trailing` sits inside the field (e.g. a
 * show/hide toggle).
 */
export function AuthField({ id, label, side, valid = false, invalid = false, trailing, children }) {
  return (
    <div className={`fld2 ${valid ? 'is-valid' : ''} ${invalid ? 'is-err' : ''}`}>
      <div className="fld2-head">
        <label htmlFor={id}><i className="ember" aria-hidden="true" />{label}</label>
        {side && <span className="fld2-side">{side}</span>}
      </div>
      <div className="fld2-body">
        {children}
        {trailing}
      </div>
    </div>
  );
}

/**
 * The primary action: a pill with a round knob. Renders an <a> when `href`
 * is given, otherwise a submit <button>.
 */
export function EnterButton({ label = 'Enter', busyLabel, busy = false, done = false, href, className = '', style }) {
  const knob = done ? <IconCheck size={22} /> : busy ? <span className="spin" aria-hidden="true" /> : <IconArrow />;
  const text = done ? 'Welcome' : busy ? (busyLabel || label) : label;
  const inner = (
    <>
      <span>{text}</span>
      <span className="enter-knob">{knob}</span>
    </>
  );
  if (href) {
    return <a href={href} className={`enter ${className}`} style={style}>{inner}</a>;
  }
  return (
    <button type="submit" className={`enter ${className}`} style={style} disabled={busy || done} aria-busy={busy || done}>
      {inner}
    </button>
  );
}

export default function AuthLayout({ children, lionRef, leaving = false }) {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  return (
    <div className={`auth-root ${leaving ? 'is-leaving' : ''}`}>
      {/* night sky behind everything, form included */}
      <StarField />
      <div className="auth-wrap">
        <div className="auth-bg" aria-hidden="true">
          <div className="auth-glow" />
          <LionField ref={lionRef} />
        </div>

        <header className="auth-top">
          <BrandTicker href="/login" size={28} />
          {!online && (
            <span className="auth-net" role="status"><i />Offline</span>
          )}
        </header>

        {/* div (not <main>): the shell's global `main` rule clips overflow-x,
            which would cut off the soft glow around a focused field. */}
        <div className="auth-main" role="main">
          <div className="auth-panel">{children}</div>
        </div>

        <footer className="auth-foot">Leo Club of SLIIT &middot; District 306 D6</footer>
      </div>
    </div>
  );
}
